const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

class DarkrimFaq extends HTMLElement {
  #activeIndex = -1;
  #defaultIndex = 0;
  #items = [];
  #panels = [];
  #reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
  #triggers = [];

  connectedCallback() {
    this.#items = Array.from(this.querySelectorAll('[data-darkrim-faq-item]'));
    this.#triggers = Array.from(this.querySelectorAll('[data-darkrim-faq-trigger]'));
    this.#panels = Array.from(this.querySelectorAll('[data-darkrim-faq-panel]'));
    this.#defaultIndex = this.#normalizeIndex(Number(this.dataset.defaultIndex));

    if (this.#items.length === 0) return;

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('shopify:block:select', this.#handleBlockSelect);
    this.#setActiveIndex(this.#defaultIndex, { immediate: true });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('shopify:block:select', this.#handleBlockSelect);
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-darkrim-faq-trigger]');
    if (!trigger || !this.contains(trigger)) return;

    const nextIndex = Number(trigger.dataset.index);
    this.#setActiveIndex(nextIndex === this.#activeIndex ? -1 : nextIndex);
  };

  #handleBlockSelect = (event) => {
    if (!(event.target instanceof Element)) return;

    const item = event.target.closest('[data-darkrim-faq-item]');
    if (!item || !this.contains(item)) return;

    this.#setActiveIndex(Number(item.dataset.index), { immediate: true });
  };

  #setActiveIndex(index, options = {}) {
    const nextIndex = this.#normalizeIndex(index, true);
    if (nextIndex === this.#activeIndex) return;

    const immediate = options.immediate || this.#reducedMotionQuery.matches;
    this.#activeIndex = nextIndex;

    this.#items.forEach((item, itemIndex) => {
      const isOpen = itemIndex === nextIndex;
      const panel = this.#panels[itemIndex];
      const trigger = this.#triggers[itemIndex];

      if (trigger) trigger.setAttribute('aria-expanded', String(isOpen));
      item.classList.toggle('is-open', isOpen);

      if (!panel) return;

      if (isOpen) {
        this.#openPanel(panel, immediate);
      } else {
        this.#closePanel(panel, immediate);
      }
    });
  }

  #openPanel(panel, immediate) {
    panel.dataset.darkrimFaqState = 'opening';
    panel.hidden = false;

    if (immediate) {
      panel.style.height = 'auto';
      panel.dataset.darkrimFaqState = 'open';
      return;
    }

    panel.style.height = '0px';
    panel.offsetHeight;
    panel.style.height = `${panel.scrollHeight}px`;

    const handleTransitionEnd = (event) => {
      if (event.target !== panel || event.propertyName !== 'height') return;

      panel.removeEventListener('transitionend', handleTransitionEnd);
      if (panel.dataset.darkrimFaqState !== 'opening') return;

      panel.style.height = 'auto';
      panel.dataset.darkrimFaqState = 'open';
    };

    panel.addEventListener('transitionend', handleTransitionEnd);
  }

  #closePanel(panel, immediate) {
    if (panel.hidden) return;

    panel.dataset.darkrimFaqState = 'closing';

    if (immediate) {
      panel.hidden = true;
      panel.style.height = '';
      panel.dataset.darkrimFaqState = 'closed';
      return;
    }

    const startHeight = panel.scrollHeight;
    if (startHeight === 0) {
      panel.hidden = true;
      panel.style.height = '';
      panel.dataset.darkrimFaqState = 'closed';
      return;
    }

    panel.style.height = `${startHeight}px`;
    panel.offsetHeight;
    panel.style.height = '0px';

    const handleTransitionEnd = (event) => {
      if (event.target !== panel || event.propertyName !== 'height') return;

      panel.removeEventListener('transitionend', handleTransitionEnd);
      if (panel.dataset.darkrimFaqState !== 'closing') return;

      panel.hidden = true;
      panel.style.height = '';
      panel.dataset.darkrimFaqState = 'closed';
    };

    panel.addEventListener('transitionend', handleTransitionEnd);
  }

  #normalizeIndex(index, allowClosed = false) {
    if (allowClosed && index === -1) return -1;
    if (!Number.isFinite(index)) return 0;

    return Math.min(Math.max(Math.trunc(index), 0), Math.max(this.#items.length - 1, 0));
  }
}

if (!customElements.get('darkrim-faq')) {
  customElements.define('darkrim-faq', DarkrimFaq);
}
